import dayjs from 'dayjs';
import { Box } from '@mui/material';
import { PortfolioDetailAndProjectionsContentProps } from '../../../common/types/plan-overview-entity';
import PortfolioCompositionContainer from '../PortfolioComposition';
import { useAppSelector } from '../../../common/hooks';
import useMarketPortfolioData from '../PortfolioComposition/useMarketPortfolioData';
import { AllocationsOverTimeTab } from '../../../containers/review-plan-details/AllocationsOverTimeTab';
import { ProjectedWealthPathChart } from '../../molecules/ProjectedWealthPathChart';
import { getGoalPriorityProbability, buildWealthPathChartData } from '../../../common/helper';
import { useTranslation } from 'react-i18next';
import { SectionHeader } from '../../../themes/styles/plan-overview';
import { iwsGoalTypeMapper } from '../../../common/constants';

export const PortfolioDetailAndProjectionsContent = ({
    selectedGoalIndex
}: PortfolioDetailAndProjectionsContentProps) => {
    const { t } = useTranslation();
    const { portfolio } = useAppSelector((state) => state.global);
    const {
        formFields,
        request,
        response,
        global: { responseGoalCalculator }
    } = useAppSelector((state) => state?.adviceGoals);
    const { goeConfig } = useAppSelector((state) => state.global);
    const selectedGoal = formFields?.[selectedGoalIndex]?.data;
    const selectedRequest = request?.[selectedGoalIndex];
    const selectedResponse = response?.[selectedGoalIndex];
    const selectedResGC = responseGoalCalculator?.[selectedGoalIndex];
    const goalPriorityValue = goeConfig['goalPriorityProbabilities'] as Record<string, number> | undefined;
    const infusionArray = selectedResGC?.['response']?.['body']?.['infusions'] || [];
    const recommendedPortfolioId =
        selectedResponse?.['response']?.['body']?.['analysisReport']?.['recommendedPortfolioId'];
    const pathReport = selectedResponse?.['response']?.['body']?.['pathReport'];
    const wealthPath = pathReport?.wealthPath;
    const goePayload = selectedResponse?.['goePayload'];
    const isRetirement = (selectedGoal?.type ?? selectedGoal?.goalType) === iwsGoalTypeMapper.retirement;
    const isIncome = (selectedGoal?.type ?? selectedGoal?.goalType) === iwsGoalTypeMapper.income;

    const { portfolioCompositionSource } = useMarketPortfolioData(selectedGoal?.goalCategory);
    const allMarketPortfolios = portfolioCompositionSource?.length ? portfolioCompositionSource : portfolio;

    const chartData = buildWealthPathChartData(wealthPath, selectedResponse?.charts?.wealthPath?.xAxis);

    const selectedGoalPriority = selectedGoal?.['goalPriority'];
    const goalPriorityProbability = getGoalPriorityProbability(goalPriorityValue, selectedGoalPriority) * 100;
    const goalAmount = isRetirement ? selectedGoal?.['targetedRetirementIncome'] : selectedGoal?.['goalAmount'];

    const goalAmtRetirement = (arr: any, accounts: any) => {
        let min = 0;
        for (let i of arr) {
            if (i < 0) {
                min = i;
                break;
            }
        }
        // We ignore Accounts and probableGoalAmt is 0, ref: AEX-1793
        let retirementIncome = 0;
        accounts?.forEach((acc: any) => {
            if (acc?.fundingType === 'incomeSources') {
                if (acc?.frequency === 'monthly') retirementIncome += 12 * acc?.recurringContribution;
                else retirementIncome += acc?.recurringContribution; //its amount from income sources
            }
        });
        return -1 * min + retirementIncome;
    };

    const currentGoalProbability = Math.round(
        Number(selectedResponse?.['response']?.['body']?.['analysisReport']?.['currentGoalProbability'] * 100)
    );
    const isRecomendation = selectedResponse?.['response']?.['body']?.['analysisReport']?.['meetGoalPriority'];
    const probableGoalAmount = isRetirement
        ? goalAmtRetirement(infusionArray, selectedRequest?.accounts)
        : selectedResGC?.response?.body?.goalAmt;

    return (
        <Box px={3} py={0}>
            <PortfolioCompositionContainer
                recommendedPortfolioId={recommendedPortfolioId}
                allMarketPortfolios={allMarketPortfolios}
                tenure={selectedGoal?.goalCategory}
            />
            <SectionHeader sx={{ paddingTop: '32px' }}>{t('LABEL_ADJUSTED_PORTFOLIO_PROJECTIONS')}</SectionHeader>
            <AllocationsOverTimeTab
                portfolioPath={pathReport?.portfolioPath}
                scenarioType={goePayload?.['scenarioType']}
                hideCard={true}
                tenure={selectedGoal?.goalCategory}
            />
            <SectionHeader sx={{ paddingTop: '32px' }}>{t('LABEL_ADJUSTED_WEALTH_PROJECTIONS')}</SectionHeader>
            <ProjectedWealthPathChart
                data={chartData}
                targetPercentile={wealthPath?.defaultPercentile}
                lowPercentile={wealthPath?.pessimisticPercentile}
                highPercentile={wealthPath?.optimisticPercentile}
                // bankruptcyYear={bankruptcyYear}
                goalPriority={selectedGoal?.goalPriority}
                goalPriorityProbability={goalPriorityProbability}
                plotLine={
                    isRetirement
                        ? {
                              year: Number(selectedGoal?.planStartRetirement?.split('-')[2]),
                              type: iwsGoalTypeMapper.retirement
                          }
                        : isIncome
                        ? {
                              year: Number(selectedGoal?.incomeStartDate?.split('-')[2]),
                              type: iwsGoalTypeMapper.income
                          }
                        : undefined
                }
                targetGoalAmount={goalAmount}
                projectedGoalAmount={probableGoalAmount}
                goalProbabilityPercentile={currentGoalProbability}
                goalTargetDate={dayjs(goePayload.endDate, 'DD-MM-YYYY').format('MM-DD-YYYY')}
                isRetirement={isRetirement || isIncome}
                isRecomendation={isRecomendation}
                hideCard={true}
            />
        </Box>
    );
};
